import type { CoasterProject, TrackBehavior, TrackSection } from '../schema/index.ts';

export const PHASE_2_NODE_LIMIT = 256;
export const PHASE_2_SCENERY_LIMIT = 128;
export const PHASE_2_MAX_TRACK_HEIGHT_M = 65;

export type TopologyStatus = 'incomplete' | 'open' | 'closed' | 'invalid';

export interface TopologyIssue {
  readonly code: string;
  readonly message: string;
  readonly severity: 'warning' | 'blocker';
  readonly nodeId?: string;
  readonly sectionId?: string;
}

export interface TopologyReport {
  readonly status: TopologyStatus;
  readonly rideable: boolean;
  readonly issues: readonly TopologyIssue[];
}

export function validateTrackTopology(project: CoasterProject): TopologyReport {
  const { nodes, sections, closed } = project.track;
  const issues: TopologyIssue[] = [];
  if (nodes.length > PHASE_2_NODE_LIMIT)
    issues.push({
      code: 'node_limit',
      message: `This editor supports up to ${PHASE_2_NODE_LIMIT} track points.`,
      severity: 'blocker',
    });
  if (project.scenery.length > PHASE_2_SCENERY_LIMIT)
    issues.push({
      code: 'scenery_limit',
      message: `This editor supports up to ${PHASE_2_SCENERY_LIMIT} scenery pieces.`,
      severity: 'blocker',
    });

  const expectedSections = closed ? nodes.length : Math.max(0, nodes.length - 1);
  if (sections.length !== expectedSections)
    issues.push({
      code: 'section_count',
      message: closed
        ? 'A closed track needs one section between every pair of points.'
        : 'An open track needs one section between each neighboring point.',
      severity: 'blocker',
    });

  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index]!;
    const from = nodes[index];
    const to = nodes[(index + 1) % Math.max(1, nodes.length)];
    if (!from || !to || section.fromNodeId !== from.id || section.toNodeId !== to.id)
      issues.push({
        code: 'section_order',
        message: `Section ${index + 1} is not connected to its neighboring track points.`,
        severity: 'blocker',
        sectionId: section.id,
      });
  }

  if (nodes.length < 4) {
    issues.push({
      code: 'needs_points',
      message:
        nodes.length === 0
          ? 'Add a station to start your track.'
          : `Add ${4 - nodes.length} more track point${nodes.length === 3 ? '' : 's'} to test ride.`,
      severity: 'blocker',
    });
    return {
      status: issues.some((issue) => issue.code === 'section_order') ? 'invalid' : 'incomplete',
      rideable: false,
      issues,
    };
  }

  const invalid = issues.some((issue) => issue.severity === 'blocker');
  if (!invalid && !closed)
    issues.push({
      code: 'open_track',
      message: 'Finish the open end back to the station before test riding.',
      severity: 'blocker',
    });
  return {
    status: invalid ? 'invalid' : closed ? 'closed' : 'open',
    rideable: !invalid && closed,
    issues,
  };
}

export function sameBehavior(a: TrackBehavior, b: TrackBehavior): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function mergeBehavior(
  incoming: TrackBehavior,
  outgoing: TrackBehavior,
): TrackBehavior | null {
  if (sameBehavior(incoming, outgoing)) return structuredClone(incoming);
  if (incoming.kind === 'normal') return structuredClone(outgoing);
  if (outgoing.kind === 'normal') return structuredClone(incoming);
  return null;
}

export function makeOrderedSection(
  id: string,
  fromNodeId: string,
  toNodeId: string,
  behavior: TrackBehavior = { kind: 'normal' },
): TrackSection {
  return { id, fromNodeId, toNodeId, behavior };
}
